import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolFlagBadges from '@/components/Tools/ToolFlagBadges';
import ToolFavoriteHeart from '@/components/Tools/ToolFavoriteHeart';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import useResponsiveness from '@/hooks/useResponsiveness';
import { paletteForSlug, formatCount } from '@/utils/agent_catalog';

/**
 * Mirrors the old `CATEGORY_COLORS[slug]` API but routes every lookup through
 * the shared `colorForSlug` hash so tool cards / lists / heroes match every
 * other catalog surface (agents, tasks, decks, models). `'default'` and
 * unknown slugs both resolve to the neutral fallback palette.
 */
export const CATEGORY_COLORS = new Proxy(
  {},
  {
    get: (_target, slug) => {
      if (typeof slug !== 'string') return undefined;
      if (slug === 'default') return paletteForSlug('');
      return paletteForSlug(slug);
    },
  },
);

// Sensible fallback icon per outputType when the tool itself doesn't provide one
export const OUTPUT_TYPE_ICON = {
  text:       'align-left',
  image:      'image',
  video:      'video',
  audio:      'volume',
  music:      'music',
  code:       'code',
  document:   'file-lines',
  '3d-model': 'cube',
};

// Human labels for the footer chip. Anything not listed falls back to the
// raw outputType with the first letter capitalised.
export const OUTPUT_TYPE_LABEL = {
  text:       'Text',
  image:      'Image',
  video:      'Video',
  audio:      'Audio',
  music:      'Music',
  code:       'Code',
  document:   'Document',
  '3d-model': '3D model',
};

export const outputTypeLabel = (outputType) => {
  if (!outputType) return null;
  if (OUTPUT_TYPE_LABEL[outputType]) return OUTPUT_TYPE_LABEL[outputType];
  const s = String(outputType);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Only surface usage once it says something. A "3 runs" footer reads as a
// warning, not social proof.
const MIN_USAGE_TO_SHOW = 25;

// Per-row column width when the grid has **not** passed a measured width (legacy
// / first paint). Web uses `calc()` for gaps; native uses slightly-under-1/N
// percentages so rows do not wrap prematurely.
const cardWidthForColumns = (cols) => Platform.select({
  web:    cols === 2 ? 'calc(50% - 6px)' : cols === 4 ? 'calc(25% - 9px)' : 'calc(33.333% - 8px)',
  default: cols === 2 ? '49%' : cols === 4 ? '24%' : '32%',
});

const legacyToolCardWidth = (isXS, isSM, isXL) => (
  (isXS || Platform.OS === 'macos')
    ? '100%'
    : isSM
      ? cardWidthForColumns(2)
      : isXL
        ? cardWidthForColumns(4)
        : cardWidthForColumns(3)
);

const isWeb = Platform.OS === 'web';

const createStyles = (colors, isXS, isSM, isXL, isAltBGColor, measuredCardWidth) => StyleSheet.create({
  toolCard: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: isXS ? 14 : 16,
    borderRadius: 16,
    backgroundColor: isAltBGColor ? colors.solid_color_tertiary : colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    width: measuredCardWidth != null && measuredCardWidth > 0
      ? measuredCardWidth
      : legacyToolCardWidth(isXS, isSM, isXL),
    minHeight: isXS ? 150 : 172,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
    }),
  },
  toolCardHovered: {
    ...(isWeb && {
      transform: 'translateY(-2px)',
      boxShadow: '0 14px 30px -18px rgba(0,0,0,0.35)',
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 14,
  },
  avatarFrame: {
    borderRadius: 14,
    overflow: 'hidden',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: -4,
    marginRight: -6,
  },
  eyebrow: {
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  name: {
    letterSpacing: -0.2,
    lineHeight: isXS ? 20 : 22,
    marginBottom: 5,
  },
  description: {
    lineHeight: isXS ? 18 : 20,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 'auto',
    paddingTop: 14,
  },
  outputChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 24,
    paddingHorizontal: 9,
    borderRadius: 999,
    backgroundColor: isAltBGColor ? colors.solid_color_secondary : colors.solid_color_tertiary,
  },
  usage: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  usageText: {
    fontVariant: ['tabular-nums'],
  },
  // Placeholder bones
  bone: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 6,
  },
});

function ToolCard({ tool, onPress, style: styleProp, measuredCardWidth = null }) {
  const { colors, theme } = useTheme();
  const { isXS, isSM, isXL } = useResponsiveness();
  const router = useRouter();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isXL, isAltBGColor, measuredCardWidth),
    [colors, isXS, isSM, isXL, isAltBGColor, measuredCardWidth]
  );

  // Some tools nest category under tool.category (populated) or expose the slug
  const categorySlug = tool?.category?.slug || (typeof tool?.category === 'string' ? tool.category : null) || 'default';
  const categoryName = tool?.category?.name || null;
  const palette = CATEGORY_COLORS[categorySlug] || CATEGORY_COLORS.default;
  const usage = Number(tool?.stats?.usageCount) || 0;
  const avatarSize = isXS ? 44 : 48;

  const handlePress = () => {
    if (onPress) {
      onPress(tool);
    } else if (tool?.slug) {
      router.push(`/tools/${tool.slug}`);
    }
  };

  return (
    <Pressable
      style={({ hovered }) => [
        styles.toolCard,
        hovered && styles.toolCardHovered,
        hovered && { borderColor: palette.fg },
        styleProp,
      ]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={tool?.name}
    >
      <View style={styles.header}>
        <View style={styles.avatarFrame}>
          <ToolAvatar
            tool={tool}
            size={avatarSize}
            iconSize={Math.round(avatarSize * 0.46)}
            borderRadius={14}
            backgroundColor={palette.bg}
            color={palette.fg}
          />
        </View>
        <View style={styles.headerRight}>
          {/* "Featured" is communicated by the spotlight section and "New" is
              set on most of the catalog, so neither says anything on a card.
              Beta still matters — it sets expectations before a run. */}
          <ToolFlagBadges tool={tool} omit={['featured', 'new']} />
          <ToolFavoriteHeart tool={tool} />
        </View>
      </View>

      {categoryName ? (
        <Text size="xxs" weight="bold" style={[styles.eyebrow, { color: palette.fg }]} numberOfLines={1}>
          {categoryName}
        </Text>
      ) : null}
      <Text
        size={isXS ? 'm' : 'l'}
        color="primary"
        weight="bold"
        style={styles.name}
        numberOfLines={2}
      >
        {tool?.name}
      </Text>
      <Text
        style={styles.description}
        size="s"
        color="secondary"
        numberOfLines={2}
      >
        {tool?.shortDescription || tool?.description}
      </Text>

      <View style={styles.footer}>
        {tool?.outputType ? (
          <View style={styles.outputChip}>
            <Icon name={OUTPUT_TYPE_ICON[tool.outputType] || 'circle'} size={11} color={colors.text_color_secondary} />
            <Text size="xs" weight="medium" color="secondary">{outputTypeLabel(tool.outputType)}</Text>
          </View>
        ) : <View />}
        {usage >= MIN_USAGE_TO_SHOW ? (
          <View style={styles.usage}>
            <Icon name="bolt" size={10} color={colors.text_color_tertiary} />
            <Text size="xs" color="tertiary" style={styles.usageText}>{formatCount(usage)} runs</Text>
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}

export function ToolCardPlaceholder({ measuredCardWidth = null } = {}) {
  const { colors, theme } = useTheme();
  const { isXS, isSM, isXL } = useResponsiveness();
  const isAltBGColor = ['sepia', 'muted', 'night', 'smoke'].includes(theme);
  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isXL, isAltBGColor, measuredCardWidth),
    [colors, isXS, isSM, isXL, isAltBGColor, measuredCardWidth]
  );
  const avatarSize = isXS ? 44 : 48;

  return (
    <View style={styles.toolCard}>
      <View style={styles.header}>
        <View style={[styles.bone, { width: avatarSize, height: avatarSize, borderRadius: 14, opacity: 0.6 }]} />
      </View>
      <View style={[styles.bone, { height: 9, width: '28%', marginBottom: 8, opacity: 0.6 }]} />
      <View style={[styles.bone, { height: 16, width: '68%', marginBottom: 10 }]} />
      <View style={[styles.bone, { height: 12, width: '100%', marginBottom: 6, opacity: 0.7 }]} />
      <View style={[styles.bone, { height: 12, width: '58%', opacity: 0.7 }]} />
      <View style={styles.footer}>
        <View style={[styles.bone, { height: 24, width: 72, borderRadius: 999, opacity: 0.6 }]} />
      </View>
    </View>
  );
}

export default React.memo(ToolCard);
